import { useCallback, useEffect, useRef, useState } from "react";
import {
  getClaimPanelPreferences, updateClaimPanelPreferences, isAbortError,
} from "../../services/apiClient.js";
import { getSessionUser } from "../../services/pageDefaults.js";
import { CLAIM_PANEL_DEFAULTS, normalizeClaimPanelPreferences } from "../../../services/claimPanelPreferences.js";
import { claimPanelLayout } from "./claimPanelLayout.js";

// Serialize preference writes even if someone leaves and reopens the workspace
// while a save is in flight. No claim data is stored in the browser.
let saveQueue = Promise.resolve();
const storageKey = (userId) => `claimPanel:${userId}:v1`;
function readPreferences(user) {
  try {
    const saved = localStorage.getItem(storageKey(user?.id));
    if (saved) return normalizeClaimPanelPreferences(JSON.parse(saved));
  } catch { /* Storage may be disabled. */ }
  return normalizeClaimPanelPreferences(user?.page_defaults?.claimPanel);
}
function remember(userId, preferences) {
  try { localStorage.setItem(storageKey(userId), JSON.stringify(preferences)); } catch { /* Optional fallback. */ }
}

export default function useClaimPanel(claimKey) {
  const user = getSessionUser();
  const userId = user?.id;
  const [preferences, setPreferences] = useState(() => readPreferences(user));
  const preferencesRef = useRef(preferences);
  const revision = useRef(0);
  const mounted = useRef(false);
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [workspace, setWorkspace] = useState(null);
  const workspaceRef = useCallback((node) => setWorkspace(node), []);

  useEffect(() => { setShow(false); }, [claimKey]);
  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    const initial = readPreferences(getSessionUser());
    preferencesRef.current = initial;
    setPreferences(initial);
    setError("");
    const version = ++revision.current;
    // Reopening the workspace during a slow save must not load the older
    // server value over the choice just made on the previous workspace.
    if (userId) saveQueue.catch(() => {}).then(() => {
      if (controller.signal.aborted) return undefined;
      return getClaimPanelPreferences({ signal: controller.signal });
    }).then((saved) => {
      if (controller.signal.aborted || revision.current !== version) return; // local choice wins over a late load
      const next = normalizeClaimPanelPreferences(saved);
      preferencesRef.current = next;
      setPreferences(next);
      remember(userId, next);
    }).catch((err) => {
      if (!isAbortError(err) && revision.current === version)
        setError("Couldn’t load your panel preference. Using this browser’s settings.");
    });
    return () => { mounted.current = false; controller.abort(); };
  }, [userId]);

  useEffect(() => {
    if (!workspace) return undefined;
    const measure = () => setAvailableWidth(workspace.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(workspace);
    return () => observer.disconnect();
  }, [workspace]);

  function change(patch) {
    const next = normalizeClaimPanelPreferences({ ...preferencesRef.current, ...patch });
    const version = ++revision.current;
    preferencesRef.current = next;
    setPreferences(next);
    setError("");
    remember(userId, next);
    saveQueue = saveQueue.catch(() => {}).then(async () => {
      if (!userId || getSessionUser()?.id !== userId) return;
      try {
        await updateClaimPanelPreferences(next);
        if (mounted.current && revision.current === version) setError("");
      } catch {
        if (mounted.current && revision.current === version)
          setError("Couldn’t save your panel preference. Kept in this browser; use Retry to save it to your account.");
      }
    });
  }

  return {
    preferences, show, error, workspaceRef,
    ...claimPanelLayout(availableWidth, preferences.width),
    open: () => setShow(true),
    close: () => { setShow(false); if (preferencesRef.current.pinned) change({ pinned: false }); },
    pin: (pinned) => {
      setShow(pinned);
      change(pinned ? { pinned: true, width: CLAIM_PANEL_DEFAULTS.width } : { pinned: false });
    },
    resize: (width) => change({ width }),
    retry: () => change({}),
  };
}
